// Oklahoma State University: public college page (#2652, round 2), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names. The
// okstate.edu sites sit behind a Cloudflare bot check, so the pages were read
// in a headless browser (the lane's standalone pw scripts):
//  - OSU's Common Data Set 2025-2026 (CDS-2025-2026-PDF.pdf, linked from the
//    Institutional Research and Analytics CDS page): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C14/C16 (no closing date, rolling decisions from July 1),
//    C21/C22 (no early decision, no early action). Its G1/G5 give 2025-2026
//    costs only.
//  - go.okstate.edu admissions: Freshman Dates and Deadlines (rolling
//    admission, scholarship deadlines; the page names no entry year, it is
//    the live page for students entering in the fall) and Freshman Admission
//    Requirements (test score policy, assured admission).
//  - OSU Scholarships and Financial Aid: the 2026-2027 in-state and
//    out-of-state on-campus cost estimates ("August 2026 to May 2027");
//    the page links the in-state one, and the out-of-state column comes from
//    go.okstate.edu/.../out-of-state-cost/previous-year-cost. The
//    office has also posted 2027-2028 estimates; the 2026-2027 figures are
//    used here, as for every other page in this batch.
//  - College Scorecard API, school id 207388 (net price by family income).
// Departure: the CDS C8 marks the SAT or ACT "recommended"; the admissions
// page says scores are optional for admission but required for most
// scholarships, and the page states both.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'OSU Common Data Set 2025-2026',
        url: 'https://ira.okstate.edu/site-files/documents/cds/CDS-2025-2026-PDF.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'OSU Admissions, Freshman Dates and Deadlines',
        url: 'https://go.okstate.edu/admissions/freshman/dates-deadlines',
        read: '2026-10-01',
    },
    requirements: {
        label: 'OSU Admissions, Freshman Admission Requirements',
        url: 'https://go.okstate.edu/admissions/freshman/admission-requirements',
        read: '2026-10-01',
    },
    costInState: {
        label: 'OSU Financial Aid, 2026-2027 Cost Estimates (in-state page)',
        url: 'https://go.okstate.edu/scholarships-financial-aid/cost-of-attendance/undergraduate-cost/in-state-cost/previous-year-cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?207388-Oklahoma-State-University-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'oklahoma-state-university',
    kbId: 'oklahoma_state_university',
    name: 'Oklahoma State University',
    shortName: 'Oklahoma State',
    aliases: ['OSU', 'OK State'],
    location: 'Stillwater, OK',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (OSU brand colors); decoration only.
    branding: {
        primary: '#FE5C00',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/oklahoma_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 28024,
        admitted: 20646,
        enrolled: 5160,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 9828, admitted: 7231 },
            { group: 'Out-of-state', applied: 16541, admitted: 13110 },
            { group: 'International', applied: 1655, admitted: 305 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'OSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'OSU has no early decision or early action plan; the Common Data Set answers no to both. Admission is rolling, and the November 1 date is a scholarship deadline, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'None: rolling admission' },
        rows: [
            { plan: 'Early Opportunity Scholarship Deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Priority Scholarship Deadline', deadline: 'February 1', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'No deadline', decision: null, binding: false },
        ],
        note: 'The OSU application opens July 1 and the Common App August 1; apply early for the most scholarship consideration. The final scholarship deadline is the Friday before classes start, and May 1 is College Decision Day. OSU does not publish decision dates.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test scores are optional for admission, but required for most OSU scholarships. You can apply and be admitted without a score, including from an unaccredited, private or home school. OSU uses only your highest score, accepts the ACT superscore only as ACT reports it, and will leave out a score that would hurt your admission or scholarship.',
        source: 'requirements',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended for fall 2027 applicants and notes that students may apply without a test score.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (30 semester hours)', amounts: [14740, 31000] },
            { item: 'Housing and food', amounts: [12500, 12500] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1430, 1430] },
            { item: 'Transportation', amounts: [2350, 2350] },
            { item: 'Personal and miscellaneous', amounts: [3570, 3570] },
        ],
        totals: [34590, 50850],
        note: 'For a student living on campus, August 2026 to May 2027. The out-of-state column is from the matching 2026-2027 Out-of-State Cost Estimates page of the same office, which differs only in tuition and fees.',
        source: 'costInState',
        where: 'in-state and out-of-state on-campus estimates',
        netPrice: {
            average: 17058,
            byIncome: [
                { income: '$0 to $30,000', amount: 12518 },
                { income: '$30,001 to $48,000', amount: 12445 },
                { income: '$48,001 to $75,000', amount: 13913 },
                { income: '$75,001 to $110,000', amount: 19296 },
                { income: 'Over $110,000', amount: 24910 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from OSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
